import { Check, PriorityHigh } from "@mui/icons-material";
import { Badge, Box, Button, Grid } from "@mui/material";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { observer } from "mobx-react-lite";
import { LabelingDialog } from "e.sic-platform.webclient/src/components/labeling/LabelingDialog";
import { useCallback, useState } from "react";
import { videoPlayerService } from "e.sic-platform.webclient/src/services/video-player/VideoPlayerService";
import { Rect, RectType } from "e.sic-platform.webclient/src/models/Rect";
import { OcrService } from "e.sic-platform.webclient/src/services/ocr/OcrService";

const OcrInputButton = observer(function OcrInputButton(props: {onClick(): void, done: boolean, textKey: string, disabled?: boolean}) {
    const {done, onClick, textKey, disabled} = props;
    return (
        <Badge badgeContent={done ? <Check /> : <PriorityHigh />} color={done ? "success" : "error"}>
            <Button
                variant="contained"
                color="primary"
                onClick={onClick}
                disabled={disabled}
                sx={{ minWidth: 300, height: 36.5, whiteSpace: "nowrap" }}
            >
                {translationService.translate(textKey)}
            </Button>
        </Badge>
    )
})

// `disabled` freezes both buttons while the file is being processed (conversion or any AI
// service in flight). Opening a dialog then is either impossible or harmful: during conversion
// there is no player to screenshot from, and during a run the regions the job was submitted
// with would be edited underneath it. The parent lifts it as soon as nothing is running, which
// is exactly the state New and Redo land in.
export default observer(function OcrInputs(props:{ocrService: OcrService, loaded?: boolean, editable?: boolean, disabled?: boolean}) {
    const {ocrService, loaded, editable, disabled} = props;
    const[rects,setRects]=useState<Map<RectType, Rect>>(new Map());
    const[rectTypes,setRectTypes]=useState<RectType[]>([]);
    const[showToggle,setShowToggle]=useState<boolean>(false);

    // A file whose OCR already succeeded normally opens its labels read-only (the dialog just
    // replays the saved region on a fresh screenshot). `editable` overrides that: during a Redo
    // the previous result is still loaded, but the labels are being re-drawn for the NEXT run,
    // so the dialog must behave exactly as it does before a run — pre-filled and saveable.
    const viewOnly = ocrService.ocrSuccessful && !editable;

    const takeScreenshot = useCallback(async() => {
        await videoPlayerService.takeScreenshotAtCurrentVideoPosition(ocrService.fileId!);
        ocrService.setScreenshotUrl(videoPlayerService.lastScreenshotUri);
    }, [ocrService.fileId]);

    const handleOpenDialog = async (rectTypes: RectType[]) => {
        const isSubsequent = !viewOnly
            && ocrService.dialogs[rectTypes[0]].coordinates != null;

        if (viewOnly) {
            const existingTimestamp = ocrService.dialogs[rectTypes[0]].timestamp;
            videoPlayerService.setVideoProgress(existingTimestamp);
            await takeScreenshot();
        } else if (isSubsequent) {
            const savedScreenshot = ocrService.dialogs[rectTypes[0]].screenshot;
            if (savedScreenshot) {
                ocrService.setScreenshotUrl(savedScreenshot);
            } else {
                videoPlayerService.setVideoProgress(ocrService.dialogs[rectTypes[0]].timestamp);
                await takeScreenshot();
                ocrService.setDialogScreenshot(rectTypes, ocrService.screenshot);
            }
        } else {
            await takeScreenshot();
        }

        setShowToggle(isSubsequent);
        setRectTypes(rectTypes);
        setRects(ocrService.getRects(rectTypes));
    };

    const handleCloseDialog = () => {
        setRectTypes([])
        setRects(new Map())
        setShowToggle(false)
    };
    const handleComplete = (_setMainInfo: boolean, mode: 'current' | 'new') => {
        if (rectTypes.length != 0 && !viewOnly && mode === 'new') {
            ocrService.setDialogScreenshot(rectTypes, ocrService.screenshot);
            ocrService.setDialogCoordinates(rectTypes, rects);
            // The time of the frame the dialog actually showed — NOT videoProgress. The latter
            // is the live player position, which keeps moving after the frame was grabbed, so
            // OK saved a start time that didn't match the frame the region was drawn on.
            ocrService.setDialogTimestamp(rectTypes, videoPlayerService.lastScreenshotTimestamp);
        }
        setRects(new Map())
        setRectTypes([])
        setShowToggle(false)
    };

    // "Loaded/view" layout is used whenever the parent renders these buttons in view mode
    // (OCR OR analysis/segmentation results), not only when OCR itself succeeded.
    const loadedState = loaded ?? ocrService.ocrSuccessful;
    // In the loaded view the parent (OcrServicePage) wraps these buttons in the
    // same Box px:2 / lg=8 grid as the Main Info table, so the column edges
    // already match the table — we only need flex alignment, no extra padding.
    const leftAlignSx = loadedState
        ? { display: "flex", justifyContent: "flex-start" }
        : undefined;
    const rightAlignSx = loadedState
        ? { display: "flex", justifyContent: "flex-end", pr: "20px" }
        : undefined;
    const itemClassName = loadedState ? undefined : "centered";

    return (
        <>
            <Grid item xs={12} md={6} padding={1} className={itemClassName} sx={leftAlignSx}>
                <OcrInputButton textKey="ocrService.textArea.type.main" onClick={() => handleOpenDialog(["main"])} done={ocrService.mainInfoDone} disabled={disabled} />
            </Grid>
            <Grid item xs={12} md={6} padding={1} className={itemClassName} sx={rightAlignSx}>
                <OcrInputButton textKey="ocrService.textArea.type.detectionsDistance" onClick={() => handleOpenDialog(["detections","distance"])} done={ocrService.detectionsDistanceDone} disabled={disabled} />
            </Grid>
            <LabelingDialog
                isVisible={rectTypes.length!=0}
                rectTypes={rectTypes}
                onClose={handleCloseDialog}
                onComplete={handleComplete}
                screenshot={ocrService.screenshot}
                rects={rects}
                setRects={setRects}
                ocrService={ocrService}
                showToggle={showToggle}
                readOnly={viewOnly}
            />
        </>
    )
})
